import { useMemo } from 'react'
import { Assets, Button } from '@nexds/web'
import { useTheme } from 'styled-components'

import { FuelData } from '@/domain/protocols/Fuel'

import { FuelProviderCard } from '../FuelProviderCard'
import {
  CloseButton,
  FuelCardsList,
  FuelContainer,
  FuelContent,
  FuelFooter,
  FuelSectionTitle,
  Header,
  Title
} from './InfoPanel.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useI18n } from '@/presentation/hooks/useI18n'
import { FuelFormStateMutator } from '@/presentation/modules/MapScreen/states/FuelFormState'
import { FuelState, FuelStateProps } from '@/presentation/modules/MapScreen/states/FuelState'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

interface FuelFeatureInfoProps {
  title: string
  adhpCode: string
  providers: FuelData[]
  onClose?: () => void
}

function FuelFeatureInfo({ title, adhpCode, providers, onClose }: FuelFeatureInfoProps) {
  const theme = useTheme()
  const { t } = useI18n()
  const fuelState = useBehaviorSubject<FuelStateProps>(FuelState)

  const activeLayer = fuelState.layer

  const filteredProviders = useMemo(() => {
    if (!activeLayer) return []
    return providers.filter((provider) => (activeLayer === 'avgas' ? provider.avgasProvider : provider.jetProvider))
  }, [providers, activeLayer])

  return (
    <FuelContainer>
      <Header>
        <Title>{title}</Title>
        <CloseButton onClick={onClose && onClose}>
          <Assets.Close size={'md'} color={theme.colors.secondaryL3} />
        </CloseButton>
      </Header>

      <FuelContent>
        <FuelSectionTitle>
          {activeLayer === 'jet-a' ? t('FUEL_INFOPANEL_PROVIDERS_JET-A') : t('FUEL_INFOPANEL_PROVIDERS_AVGAS')}
        </FuelSectionTitle>

        <FuelCardsList>
          {filteredProviders.map((provider, index) => (
            <FuelProviderCard
              key={`${provider.provider}-${index}`}
              adhpCode={adhpCode}
              provider={provider}
              activeLayer={activeLayer}
            />
          ))}
        </FuelCardsList>
      </FuelContent>

      <FuelFooter>
        <Button
          label={t('FUEL_INFOPANEL_REPORT-PRICE')}
          variant="filled"
          color="primary"
          size="sm"
          fullWidth
          onPress={() => {
            if (!activeLayer) return
            const providerOptions = [...new Set(filteredProviders.map((provider) => provider.provider).filter(Boolean))]

            sendMetrics(MetricsEvents.FUEL_REPORT_PRICE_CLICKED, {
              adhpCode,
              fuelType: activeLayer,
              providerCount: providerOptions.length
            })

            FuelFormStateMutator.showRegisterPrice(adhpCode, activeLayer, providerOptions)
          }}
        />
        <Button
          label={t('TOOLBAR_FUEL_ITEM_REGISTER-SUPPLIER')}
          variant="outline"
          color="secondary"
          size="sm"
          leftIcon="Plus"
          fullWidth
          onPress={() => {
            if (!activeLayer) return

            sendMetrics(MetricsEvents.FUEL_REGISTER_PROVIDER_CLICKED, {
              origin: 'fuel-feature-info',
              adhpCode,
              fuelType: activeLayer
            })

            FuelFormStateMutator.showRegisterProviderFromFeatureInfo(adhpCode, activeLayer)
          }}
        />
      </FuelFooter>
    </FuelContainer>
  )
}

export { FuelFeatureInfo }
